/**
 * @fileoverview Shared timeout constants used across the application
 *
 * This file centralizes all timeout-related magic numbers to improve code
 * maintainability and consistency. All values are in milliseconds unless
 * otherwise specified.
 *
 * @module shared/constants/timeouts
 */

// ═══════════════════════════════════════════════════════════════
// UI FEEDBACK TIMEOUTS
// ═══════════════════════════════════════════════════════════════

/**
 * How long to display temporary success/info messages
 * Used for toast notifications, success badges, etc.
 */
export const UI_MESSAGE_DISPLAY_MS = 3000; // 3 seconds

/**
 * How long to display action confirmation messages
 * Used for form submissions, settings changes, etc.
 */
export const ACTION_MESSAGE_DISPLAY_MS = 5000; // 5 seconds

/**
 * Debounce delay for user input validation
 * Prevents excessive validation calls while typing
 */
export const INPUT_DEBOUNCE_MS = 300; // 300ms

/**
 * Delay before showing validation errors
 * Gives users time to correct input before showing error
 */
export const VALIDATION_DELAY_MS = 100; // 100ms

// ═══════════════════════════════════════════════════════════════
// ASSET LOADING TIMEOUTS
// ═══════════════════════════════════════════════════════════════

/**
 * Timeout for converting SVG logos to PNG format
 * Used in PDF generation and image processing
 */
export const LOGO_CONVERSION_TIMEOUT_MS = 3000; // 3 seconds

/**
 * Timeout for loading external images/assets
 */
export const IMAGE_LOAD_TIMEOUT_MS = 5000; // 5 seconds

// ═══════════════════════════════════════════════════════════════
// WEBSOCKET TIMEOUTS
// ═══════════════════════════════════════════════════════════════

/**
 * WebSocket idle timeout - close connection after inactivity
 * 10 minutes of no messages or activity (matches AWS API Gateway max lifetime)
 */
export const WEBSOCKET_IDLE_TIMEOUT_MS = 600000; // 10 minutes

/**
 * Proactive connection renewal threshold.
 * A new connection is opened in the background at this point so the swap
 * is seamless before the 10-minute AWS hard disconnect.
 */
export const WEBSOCKET_RENEWAL_THRESHOLD_MS = 540000; // 9 minutes

/**
 * How long without any received message before we probe the connection.
 * If no server message arrives for this period during active generation,
 * an application-level ping is sent to test liveness.
 */
export const WEBSOCKET_STALE_ACTIVITY_THRESHOLD_MS = 60000; // 1 minute

/**
 * WebSocket connection establishment timeout
 */
export const WEBSOCKET_CONNECTION_TIMEOUT_MS = 10000; // 10 seconds

/**
 * WebSocket ping/keep-alive interval
 */
export const WEBSOCKET_PING_INTERVAL_MS = 25000; // 25 seconds

/**
 * Grace period before closing WebSocket when no active users
 * Prevents unnecessary reconnections during quick component remounts
 */
export const WEBSOCKET_GRACE_PERIOD_MS = 5000; // 5 seconds

/**
 * Delay before initializing WebSocket connection
 */
export const WEBSOCKET_INIT_DELAY_MS = 100; // 100ms

/**
 * Base delay for WebSocket reconnection (exponential backoff)
 */
export const WEBSOCKET_RECONNECT_BASE_DELAY_MS = 1000; // 1 second

/**
 * Maximum delay between WebSocket reconnection attempts
 */
export const WEBSOCKET_RECONNECT_MAX_DELAY_MS = 30000; // 30 seconds

// ═══════════════════════════════════════════════════════════════
// API/NETWORK TIMEOUTS
// ═══════════════════════════════════════════════════════════════

/**
 * Default API request timeout
 */
export const API_REQUEST_TIMEOUT_MS = 30000; // 30 seconds

/**
 * Session warning display duration
 */
export const SESSION_WARNING_TIMEOUT_MS = 60000; // 1 minute

// ═══════════════════════════════════════════════════════════════
// CACHE TTL (Time To Live)
// ═══════════════════════════════════════════════════════════════

/**
 * Very short cache TTL for frequently changing data
 */
export const CACHE_TTL_SHORT_MS = 30000; // 30 seconds

/**
 * Standard cache TTL for moderately dynamic data
 */
export const CACHE_TTL_MEDIUM_MS = 300000; // 5 minutes

/**
 * Long cache TTL for relatively static data
 */
export const CACHE_TTL_LONG_MS = 600000; // 10 minutes

// ═══════════════════════════════════════════════════════════════
// ANIMATION/TRANSITION DELAYS
// ═══════════════════════════════════════════════════════════════

/**
 * Short delay for CSS transitions and animations
 */
export const ANIMATION_SHORT_MS = 200; // 200ms

/**
 * Standard delay for CSS transitions and animations
 */
export const ANIMATION_STANDARD_MS = 300; // 300ms

/**
 * Long delay for complex animations
 */
export const ANIMATION_LONG_MS = 500; // 500ms

// ═══════════════════════════════════════════════════════════════
// POLLING INTERVALS
// ═══════════════════════════════════════════════════════════════

/**
 * Frequent polling interval for real-time updates
 */
export const POLL_INTERVAL_FREQUENT_MS = 2000; // 2 seconds

/**
 * Standard polling interval
 */
export const POLL_INTERVAL_STANDARD_MS = 5000; // 5 seconds

/**
 * Slow polling interval for background updates
 */
export const POLL_INTERVAL_SLOW_MS = 10000; // 10 seconds

/**
 * Idle monitoring check interval
 * How often to check if connection has been idle
 */
export const IDLE_CHECK_INTERVAL_MS = 60000; // 1 minute

// ═══════════════════════════════════════════════════════════════
// RETRY DELAYS
// ═══════════════════════════════════════════════════════════════

/**
 * Short retry delay for quick retries
 */
export const RETRY_DELAY_SHORT_MS = 1000; // 1 second

/**
 * Medium retry delay
 */
export const RETRY_DELAY_MEDIUM_MS = 3000; // 3 seconds

/**
 * Long retry delay for rate-limited operations
 */
export const RETRY_DELAY_LONG_MS = 5000; // 5 seconds

// ═══════════════════════════════════════════════════════════════
// STREAM PROCESSING TIMEOUTS
// ═══════════════════════════════════════════════════════════════

/**
 * Time to wait for next chunk before considering stream complete
 */
export const STREAM_CHUNK_TIMEOUT_MS = 4000; // 4 seconds

/**
 * Time to wait for next chunk with large buffer before completion
 */
export const STREAM_LARGE_BUFFER_TIMEOUT_MS = 2000; // 2 seconds

/**
 * Safety timeout for stream processing
 */
export const STREAM_SAFETY_TIMEOUT_MS = 20000; // 20 seconds

/**
 * Time since last chunk to trigger completion
 */
export const STREAM_COMPLETION_THRESHOLD_MS = 12000; // 12 seconds

/**
 * Maximum time for stream completion check
 */
export const STREAM_MAX_COMPLETION_TIME_MS = 35000; // 35 seconds

/**
 * Interval for checking stream completion
 */
export const STREAM_COMPLETION_CHECK_INTERVAL_MS = 1000; // 1 second
